import React, { useMemo } from 'react';
import type { TCredentialingPanelAction, TCredentialingActionItem } from 'types/credentialing';
import ActionNeededCard from '../ActionNeededCard';
import ChecklistCard from '../ChecklistCard';
import { getPaymentActionCopy } from '../../statusMeta';
import type { ChecklistGroup } from '../ChecklistCard';
import s from './s.module.less';

interface Props {
    actionNeededPanels?: TCredentialingPanelAction[];
    onUpdateProfile?: (item: TCredentialingActionItem) => void;
    onFixPayment?: (item: TCredentialingActionItem) => void;
    onConfirmSignature?: (item: TCredentialingActionItem, credentialingPayerId: number) => void;
}

const buildChecklistGroups = (actionPanels: TCredentialingPanelAction[], types: string[]): ChecklistGroup[] => {
    const seenRefIds = new Set<number>();
    return actionPanels
        .map((p) => {
            const items = p.actionItems
                .filter((it) => {
                    if (!types.includes(it.type || '')) return false;
                    if (it.refId != null && seenRefIds.has(it.refId)) return false;
                    if (it.refId != null) seenRefIds.add(it.refId);
                    return true;
                })
                .map((it) => ({ reason: it.reason || 'Action required', actionItem: it }));
            if (items.length === 0) return null;
            return { payer: p.payerName, state: p.payerState, items };
        })
        .filter(Boolean) as ChecklistGroup[];
};

const ActionNeededBanner = ({ actionNeededPanels = [], onUpdateProfile, onFixPayment, onConfirmSignature }: Props) => {
    const docRfiGroups = useMemo(
        () => buildChecklistGroups(actionNeededPanels, ['DOCUMENT', 'RFI']),
        [actionNeededPanels],
    );
    const paymentItems = useMemo(
        () =>
            actionNeededPanels.flatMap((p) =>
                p.actionItems.filter((it) => it.type === 'PAYMENT').map((it) => ({ ...it, payerName: p.payerName })),
            ),
        [actionNeededPanels],
    );
    const signatureItems = useMemo(
        () =>
            actionNeededPanels
                .filter((p) => p.actionItems.some((it) => it.type === 'SIGNATURE'))
                .map((p) => ({
                    payerName: p.payerName,
                    credentialingPayerId: p.credentialingPayerId,
                    item: p.actionItems.find((it) => it.type === 'SIGNATURE')!,
                })),
        [actionNeededPanels],
    );
    if (actionNeededPanels.length === 0) return null;

    const blocks: React.ReactNode[] = [];

    if (paymentItems.length > 0) {
        const payment = getPaymentActionCopy(paymentItems);
        blocks.push(
            <div key="payment" className={s.pinnedActions}>
                <ActionNeededCard
                    name={payment.name}
                    detail={payment.detail}
                    btnText={payment.btnText}
                    onAction={() => onFixPayment?.(payment.item)}
                />
            </div>,
        );
    }

    signatureItems.forEach((sig) => {
        blocks.push(
            <div key={`signature-${sig.credentialingPayerId}`} className={s.pinnedActions}>
                <ActionNeededCard
                    name={`${sig.payerName} approved you`}
                    detail={`${sig.payerName} emailed a contract to your inbox. Sign it, then mark it as signed here to move on to billing setup.`}
                    btnText="I've signed it"
                    onAction={() => onConfirmSignature?.(sig.item, sig.credentialingPayerId)}
                />
            </div>,
        );
    });

    if (docRfiGroups.length > 0) {
        const totalItems = docRfiGroups.reduce((sum, g) => sum + g.items.length, 0);
        const subtitle =
            docRfiGroups.length === 1
                ? `Our team reviewed your application. ${docRfiGroups[0].payer} needs a few updates before we can continue.`
                : `Our team reviewed your application. ${docRfiGroups.length} payers need updates (${totalItems} item${totalItems > 1 ? 's' : ''} total).`;
        blocks.push(
            <ChecklistCard
                key="checklist"
                title="Action needed"
                subtitle={subtitle}
                groups={docRfiGroups}
                onItemAction={onUpdateProfile}
            />,
        );
    }

    if (blocks.length === 0) return null;

    return <>{blocks}</>;
};

export default ActionNeededBanner;
